import { FC } from 'react';
import { Link } from 'react-router-dom';

import {
    BodyText,
    Card,
    Eyebrow,
    Headline,
    Icon,
    Layout,
    Link as AnvilLink,
    Page,
    Stack,
    StatusLight,
    Tag,
} from '@servicetitan/design-system';

import { Paths } from '../../paths';

export const CustomerIntegrationPathsPage: FC = () => {
    return (
        <Page
            header={
                <Stack direction="column" spacing={1}>
                    <Headline el="h1" size="large">
                        Customer Integration Paths
                    </Headline>
                    <BodyText>
                        Choose the integration approach that best fits your needs and security
                        requirements.
                    </BodyText>
                </Stack>
            }
        >
            <Layout type="2Col" spacing="relaxed">
                <Layout.Section>
                    <Stack direction="column" spacing={3}>
                        <StatusLight color="success">
                            <Eyebrow className="c-green-500">Allowed Integrations</Eyebrow>
                        </StatusLight>

                        <Card>
                            <Stack direction="column" spacing={3}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Headline el="h2" size="medium" className="m-y-0">
                                        Certified Apps
                                    </Headline>
                                    <Tag color="success" subtle>
                                        Best Practice
                                    </Tag>
                                </Stack>
                                <BodyText>
                                    A Certified App is built by a third party and has gone through a
                                    rigorous review by ServiceTitan to ensure it meets best
                                    practices for security, reliability, and performance. These apps
                                    integrate seamlessly without requiring you to share App Keys.
                                </BodyText>
                                <Stack.Item>
                                    <Eyebrow className="m-b-1">To Get Started</Eyebrow>
                                    <BodyText>
                                        Visit{' '}
                                        <AnvilLink
                                            primary
                                            href="https://marketplace.servicetitan.com"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            marketplace.servicetitan.com
                                        </AnvilLink>
                                        .
                                    </BodyText>
                                </Stack.Item>
                            </Stack>
                        </Card>

                        <Card>
                            <Stack direction="column" spacing={3}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Headline el="h2" size="medium" className="m-y-0">
                                        Customer-Built Apps
                                    </Headline>
                                    <Tag color="success" subtle>
                                        Supported
                                    </Tag>
                                </Stack>
                                <BodyText>
                                    A Customer-Built App is created, hosted, and maintained by you.
                                    You can use your own App Keys to connect your integration
                                    software to ServiceTitan, without sharing those keys with
                                    external software you don’t control.
                                </BodyText>
                                <Stack.Item>
                                    <Eyebrow className="m-b-1">To Get Started</Eyebrow>
                                    <ol className="m-y-0">
                                        <li>
                                            <BodyText>
                                                Ensure you are on the <strong>Works</strong> or{' '}
                                                <strong>Ent Plus</strong> package.
                                            </BodyText>
                                        </li>
                                        <li>
                                            <BodyText>
                                                Visit the{' '}
                                                <Link
                                                    className="Link Link--blue"
                                                    to={Paths.requestAccess}
                                                >
                                                    Request Access page
                                                </Link>{' '}
                                                to request an integration environment as a
                                                ServiceTitan customer.
                                            </BodyText>
                                        </li>
                                    </ol>
                                </Stack.Item>
                            </Stack>
                        </Card>
                    </Stack>
                </Layout.Section>

                <Layout.Section>
                    <Stack direction="column" spacing={3}>
                        <StatusLight color="critical" className="c-red-600">
                            <Eyebrow className="c-red-600">Not Allowed Integrations</Eyebrow>
                        </StatusLight>

                        <Card>
                            <Stack direction="column" spacing={3}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Headline el="h2" size="medium" className="m-y-0">
                                        Tunneled Apps
                                    </Headline>
                                    <Tag color="critical" subtle>
                                        Not Allowed
                                    </Tag>
                                </Stack>

                                <Stack.Item>
                                    <Eyebrow className="m-b-1">What is Tunneling?</Eyebrow>
                                    <BodyText>
                                        Tunneling occurs when a third-party application &mdash; one
                                        not owned or controlled by you &mdash; is granted access to
                                        your App Key to establish an integration.
                                    </BodyText>
                                </Stack.Item>

                                <Stack.Item>
                                    <Eyebrow className="m-b-1">How This Can Occur</Eyebrow>
                                    <ul className="m-y-0">
                                        <li>
                                            <BodyText>
                                                A third party asks you to create an app and share
                                                its App Key.
                                            </BodyText>
                                        </li>
                                        <li>
                                            <BodyText>
                                                You grant a third-party user permissions to create
                                                apps, allowing them to generate and use the App Key.
                                            </BodyText>
                                        </li>
                                    </ul>
                                </Stack.Item>

                                <Stack.Item>
                                    <Eyebrow className="m-b-1">Why This is a Concern</Eyebrow>
                                    <Stack direction="column" spacing={2}>
                                        <Stack direction="row" alignItems="center" spacing={3}>
                                            <Icon className="c-red-600" name="person" />
                                            <div>
                                                <BodyText bold className="c-red-600">
                                                    Identity Masking
                                                </BodyText>
                                                <BodyText>
                                                    Third parties can operate under your company’s
                                                    identity.
                                                </BodyText>
                                            </div>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={3}>
                                            <Icon className="c-red-600" name="security" />
                                            <div>
                                                <BodyText bold className="c-red-600">
                                                    Ungoverned Data Access
                                                </BodyText>
                                                <BodyText>
                                                    Neither you nor ServiceTitan can monitor or
                                                    control what data is being accessed or how it’s
                                                    used.
                                                </BodyText>
                                            </div>
                                        </Stack>
                                    </Stack>
                                </Stack.Item>
                            </Stack>
                        </Card>
                    </Stack>
                </Layout.Section>
            </Layout>
        </Page>
    );
};
CustomerIntegrationPathsPage.displayName = 'CustomerIntegrationPathsPage';
